import React, { useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Box, Typography, InputBase, Stack, Skeleton } from "@mui/material";
import { keyframes } from "@mui/system";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import CorporateFareRoundedIcon from "@mui/icons-material/CorporateFareRounded";

import Dialog from "../../../components/dialog/Dialog";
import Content from "../../../components/Content";
import { GetCustomers } from "../../../hooks/useCustomerData";
import { CreateCustomerModal } from "../../../modals/customer/CreateCustomerModal";
import { useIsSuperAdmin } from "../../../hooks/useIsSuperAdmin";

const floatOrb = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50%      { transform: translate3d(-14px, 20px, 0) scale(1.04); }
`;

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";
const NAVY = "#10324a";

const AVATAR_GRADIENTS = [
  ["#8a1125", "#c23b54"],
  ["#10324a", "#2d5a7e"],
  ["#3d2645", "#6b4a7d"],
  ["#4a3218", "#8a5a2b"],
  ["#1f3d2e", "#3d6b52"],
  ["#2c1810", "#5c3820"],
  ["#0a1628", "#2d3e5a"],
];

const hashString = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const gradientFor = (name) =>
  AVATAR_GRADIENTS[hashString(name) % AVATAR_GRADIENTS.length];

const initialsFor = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Avatar = ({ name, size = 46 }) => {
  const [a, b] = gradientFor(name);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "14px",
        background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `0 10px 22px -12px ${a}55`,
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: size * 0.36,
          letterSpacing: "-0.02em",
          color: "#fff",
        }}
      >
        {initialsFor(name)}
      </Typography>
    </Box>
  );
};

const OrganisationRow = ({ organisation, onSelect }) => (
  <Box
    onClick={() => onSelect(organisation.id)}
    sx={{
      display: "grid",
      gridTemplateColumns: "auto 1fr auto auto",
      alignItems: "center",
      gap: 3,
      px: 3,
      py: 2.5,
      cursor: "pointer",
      position: "relative",
      borderBottom: `1px solid ${LINE_SOFT}`,
      transition: "background 0.3s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "14%",
        bottom: "14%",
        width: "2px",
        background: BRAND,
        transform: "scaleY(0)",
        transformOrigin: "center",
        transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        background: "rgba(138, 17, 37, 0.025)",
        "&::before": { transform: "scaleY(1)" },
        "& .row-arrow": { opacity: 1, transform: "translateX(0)" },
        "& .row-name": { color: BRAND },
      },
    }}
  >
    <Avatar name={organisation.organisation_name || "?"} />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        className="row-name"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: "17px",
          fontWeight: 600,
          color: INK,
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          transition: "color 0.25s ease",
        }}
      >
        {organisation.organisation_name || "Namnlös organisation"}
      </Typography>
    </Box>
    <Box
      sx={{
        display: { xs: "none", sm: "inline-flex" },
        alignItems: "center",
        gap: 0.75,
        px: 1.75,
        height: 32,
        borderRadius: "999px",
        border: `1px solid ${LINE}`,
        background: "rgba(10,22,40,0.025)",
        color: INK_MUTED,
        fontSize: "13px",
        fontWeight: 600,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      <PlaceRoundedIcon sx={{ fontSize: 15 }} />
      {organisation.municipality || "—"}
    </Box>
    <Box
      className="row-arrow"
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: `1px solid ${LINE}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 0,
        transform: "translateX(-6px)",
        transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        color: BRAND,
      }}
    >
      <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
    </Box>
  </Box>
);

const EmptyState = ({ onCreate, hasQuery }) => (
  <Box
    sx={{
      py: 10,
      textAlign: "center",
      border: `1px dashed ${LINE}`,
      borderRadius: "16px",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(250,248,245,0.6))",
    }}
  >
    <Box
      sx={{
        width: 68,
        height: 68,
        mx: "auto",
        mb: 3,
        borderRadius: "20px",
        background: `linear-gradient(135deg, ${NAVY}, #1a4a6e)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 20px 40px -20px rgba(16,50,74,0.4)",
      }}
    >
      <CorporateFareRoundedIcon sx={{ color: "#fff", fontSize: 32 }} />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "1.75rem",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.025em",
        mb: 1,
      }}
    >
      {hasQuery ? "Inga träffar" : "Inga organisationer ännu"}
    </Typography>
    <Typography
      sx={{
        fontSize: "14.5px",
        color: INK_MUTED,
        maxWidth: 420,
        mx: "auto",
        mb: 4,
        lineHeight: 1.6,
      }}
    >
      {hasQuery
        ? "Prova en annan sökterm för att se alla organisationer."
        : "Skapa den första organisationen för att börja lägga upp grupper och användare."}
    </Typography>
    {!hasQuery && (
      <Box
        onClick={onCreate}
        component="button"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 3,
          py: 1.5,
          background: BRAND,
          color: "#fff",
          border: "none",
          borderRadius: "10px",
          fontFamily: '"DM Sans", sans-serif',
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.25s ease",
          boxShadow: "0 10px 24px -12px rgba(138,17,37,0.5)",
          "&:hover": { background: "#6b0d1d", transform: "translateY(-1px)" },
        }}
      >
        <AddRoundedIcon sx={{ fontSize: 18 }} />
        Skapa organisation
      </Box>
    )}
  </Box>
);

export const OrganisationsView = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, loading: authLoading } = useIsSuperAdmin();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: customersData, isLoading } = GetCustomers();

  const organisations = useMemo(
    () => (Array.isArray(customersData?.data) ? customersData.data : []),
    [customersData]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return organisations;
    return organisations.filter(
      (o) =>
        (o.organisation_name || "").toLowerCase().includes(q) ||
        (o.municipality || "").toLowerCase().includes(q)
    );
  }, [organisations, search]);

  if (authLoading) return null;
  if (!isSuperAdmin) return <Navigate to="/admin/overview" replace />;

  const hasQuery = !!search.trim();

  return (
    <Content>
      <Box
        sx={{
          position: "relative",
          px: { xs: 1, md: 2 },
          pt: 2,
          pb: 6,
          minHeight: "100%",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            right: -80,
            width: 520,
            height: 520,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(138,17,37,0.09), rgba(138,17,37,0) 62%)",
            filter: "blur(8px)",
            pointerEvents: "none",
            animation: `${floatOrb} 14s ease-in-out infinite`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1, mb: 5 }}>
          <Box
            sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, mb: 3 }}
          >
            <Box
              sx={{
                width: 28,
                height: "1.5px",
                background: `linear-gradient(90deg, ${BRAND}, rgba(138,17,37,0))`,
              }}
            />
            <Typography
              sx={{
                fontSize: "10.5px",
                fontWeight: 700,
                letterSpacing: "0.26em",
                textTransform: "uppercase",
                color: BRAND,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Superadmin · Alla organisationer
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 3,
            }}
          >
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: "2.5rem", md: "3.75rem" },
                  lineHeight: 0.98,
                  letterSpacing: "-0.04em",
                  color: INK,
                  mb: 2.5,
                }}
              >
                Organisationer
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: "1rem",
                  lineHeight: 1.7,
                  color: INK_MUTED,
                  maxWidth: 560,
                }}
              >
                Lägg upp nya organisationer och klicka in på en organisation för
                att skapa grupper och användare åt dem.
              </Typography>
            </Box>

            <Box
              component="button"
              onClick={() => setIsCreateModalOpen(true)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                px: 2.75,
                py: 1.625,
                background: INK,
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                boxShadow: "0 14px 32px -14px rgba(10,22,40,0.45)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 22px 44px -18px rgba(10,22,40,0.55)",
                },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 18 }} />
              Ny organisation
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            height: 44,
            maxWidth: 340,
            mb: 3,
            background: "#fff",
            border: `1px solid ${LINE}`,
            borderRadius: "12px",
            transition: "all 0.25s ease",
            "&:focus-within": {
              borderColor: "rgba(10,22,40,0.28)",
              boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
            },
          }}
        >
          <SearchRoundedIcon sx={{ fontSize: 18, color: INK_MUTED }} />
          <InputBase
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sök organisation eller kommun..."
            sx={{
              flex: 1,
              fontSize: "14px",
              fontFamily: '"DM Sans", sans-serif',
              color: INK,
              "& input::placeholder": { color: INK_MUTED, opacity: 1 },
            }}
          />
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "11.5px",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            {isLoading
              ? "Läser in"
              : `Visar ${filtered.length} ${
                  filtered.length === 1 ? "organisation" : "organisationer"
                }${hasQuery ? ` av ${organisations.length}` : ""}`}
          </Typography>
          <Box sx={{ flex: 1, height: "1px", background: LINE }} />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          {isLoading ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    px: 3,
                    py: 2.5,
                    borderBottom: i < 2 ? `1px solid ${LINE_SOFT}` : "none",
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={46}
                    height={46}
                    sx={{ borderRadius: "14px" }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="38%" height={20} />
                  </Box>
                  <Skeleton
                    variant="rounded"
                    width={90}
                    height={28}
                    sx={{ borderRadius: "999px" }}
                  />
                </Box>
              ))}
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyState
              onCreate={() => setIsCreateModalOpen(true)}
              hasQuery={hasQuery}
            />
          ) : (
            <Stack
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(10,22,40,0.02)",
                "& > *:last-child": { borderBottom: "none" },
              }}
            >
              {filtered.map((organisation) => (
                <OrganisationRow
                  key={organisation.id}
                  organisation={organisation}
                  onSelect={(id) => navigate(`/admin/organisations/${id}`)}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog
        title="Skapa organisation"
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      >
        <CreateCustomerModal
          closeCreateModal={() => setIsCreateModalOpen(false)}
        />
      </Dialog>
    </Content>
  );
};

export default OrganisationsView;
